import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useGetInvoiceApprovers, { InvoiceApproverItem } from 'src/services/useGetInvoiceApprovers';
import useSubmitForApprovalApi from 'src/services/useSubmitForApprovalApi';
import { useContextSelector } from 'use-context-selector';
import InvoiceApproversTable from './InvoiceApproversTable';
import { InvoiceContext } from './InvoiceDetailsContext';
import { InformationComponent, WarningComponent } from './SubmitForApprovalModalContents';
import useInvoiceButtonState from './useInvoiceButtonState';

interface SubmitForApprovalDialogProps {}

const SubmitForApprovalDialog: FC<SubmitForApprovalDialogProps> = () => {
  const [initialCheckBox, setInitialCheckBox] = useState(false);
  const { value: isModalOpen, setFalse: handleCloseModal, setTrue: handleOpenModal } = useBoolean();
  const { invoiceApprovers, isApproversLoading, setInvoiceApprovers, getInvoiceApprovers } =
    useGetInvoiceApprovers();

  const [selectedApprover, setSelectedApprover] = useState<InvoiceApproverItem>(
    {} as InvoiceApproverItem
  );
  const [messages, buttonState] = useInvoiceButtonState('SubmitForApproval');
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const hasDuplicateInvoiceNumber = useContextSelector(
    InvoiceContext,
    (value) => value.hasDuplicateInvoiceNumber
  );
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);
  const getMessages = useContextSelector(InvoiceContext, (value) => value.getMessages);
  const isInvoiceActionButtonDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceActionButtonDisabled
  );

  const [modalStepCount, setModalStepCount] = useState(0);

  const { submitForApproval, isSubmittingForApproval } = useSubmitForApprovalApi(
    [Number(invoiceId)],
    selectedApprover?.clientId || 0
  );

  const handleSubmit = useCallback(async () => {
    try {
      const res = await submitForApproval();
      if (!(res as any)?.unProcessed?.length) {
        setModalStepCount(2);
        refreshPage?.();
      } else {
        toast.error('Document Submission Unsuccessful');
        setModalStepCount(0);
        handleCloseModal();
      }
    } catch (e) {
      console.error(e);
    }
  }, [handleCloseModal, refreshPage, submitForApproval]);

  const openModal = useCallback(() => {
    setInitialCheckBox(false);
    if (hasDuplicateInvoiceNumber) {
      setModalStepCount(0);
    } else {
      setModalStepCount(1);
    }
    handleOpenModal();
  }, [handleOpenModal, hasDuplicateInvoiceNumber]);

  const infoList = useMemo(() => {
    const infoList: string[] = [];
    const { totalCostAmt } = invoice;
    if (totalCostAmt === 0) {
      infoList.push('This document has a zero dollar balance.');
    }
    return infoList;
  }, [invoice]);

  const SubmitForApprovalModal = useMemo(
    () => (
      <div className="px-2">
        {hasDuplicateInvoiceNumber ? (
          <div className="mb-2">
            <WarningComponent
              initialCheckBox={initialCheckBox}
              setInitialCheckBox={setInitialCheckBox}
            />
          </div>
        ) : (
          ''
        )}
        {infoList.length ? <InformationComponent content={infoList} key="info" /> : ''}
        <br />
        <p>Continue to Submit for Approval?</p>
      </div>
    ),
    [infoList, initialCheckBox, hasDuplicateInvoiceNumber]
  );

  const modalSteps = useMemo(
    () => [
      {
        title: 'Submit For Approval',
        content: SubmitForApprovalModal,
        isConfirmationModal: false,
        clickFunction: () => setModalStepCount(1),
      },
      {
        title: 'Submit For Approval',
        content: (
          <InvoiceApproversTable
            setSelectedApprover={setSelectedApprover}
            invoiceApprovers={invoiceApprovers}
            isApproversLoading={isApproversLoading}
            setInvoiceApprovers={setInvoiceApprovers}
          />
        ),
        isConfirmationModal: false,
        clickFunction: handleSubmit,
      },
      {
        title: 'Document Submitted Successfully',
        content: <b>Document Submitted Successfully</b>,
        isConfirmationModal: true,
      },
    ],
    [
      SubmitForApprovalModal,
      handleSubmit,
      invoiceApprovers,
      isApproversLoading,
      setInvoiceApprovers,
    ]
  );

  useEffect(() => {
    if (isModalOpen && !invoiceApprovers.length) {
      getInvoiceApprovers();
    }
  }, [getInvoiceApprovers, invoiceApprovers.length, isModalOpen]);

  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type="Error" contents={[...getMessages(buttonState, messages)]} />
          </div>
        }
        show={isInvoiceActionButtonDisabled(buttonState)}
      >
        <Button
          disabled={isInvoiceActionButtonDisabled(buttonState)}
          buttonState={buttonState}
          color="submit"
          onClick={openModal}
        >
          Submit for Approval
        </Button>
      </PopUp>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title={modalSteps?.[modalStepCount]?.title}
        id="modal"
      >
        <div className="p-4 min-w-[500px]">{modalSteps?.[modalStepCount]?.content}</div>
        <DialogActionsBar>
          <div className="flex flex-row items-center justify-end gap-2">
            {!modalSteps?.[modalStepCount]?.isConfirmationModal &&
              (modalStepCount !== 1 || invoiceApprovers?.length > 0) && (
                <Button
                  color="info"
                  onClick={modalSteps?.[modalStepCount]?.clickFunction}
                  disabled={
                    (!hasDuplicateInvoiceNumber ? false : !initialCheckBox) ||
                    (modalStepCount === 1 && isEmpty(selectedApprover))
                  }
                  loading={isSubmittingForApproval}
                >
                  Submit for Approval
                </Button>
              )}
            <Button color="info" onClick={handleCloseModal}>
              {modalSteps?.[modalStepCount]?.isConfirmationModal ||
              (modalStepCount === 1 && invoiceApprovers?.length === 0)
                ? 'OK'
                : 'Cancel'}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default SubmitForApprovalDialog;
